import React, { useState, useMemo, useId } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartEvent, ActiveElement, ChartOptions } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Search, Eye, GitCompare } from 'lucide-react';
import { formatStatus, formatCategory } from '../../data/validationData';
import { FilterStatus, FilterType, FilterCategory, ValidationProject, ValidationStatus, ProjectType } from '../../types/validation';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

interface ControlTowerProps {
  projects: ValidationProject[];
  onViewProject: (projectId: string) => void;
  onViewComparison: (projectId: string) => void;
}

const ControlTower: React.FC<ControlTowerProps> = ({ projects, onViewProject, onViewComparison }) => {
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('');
  const [typeFilter, setTypeFilter] = useState<FilterType>('');
  const [categoryFilter, setCategoryFilter] = useState<FilterCategory>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Generate unique IDs for accessibility
  const statusFilterId = useId();
  const typeFilterId = useId();
  const categoryFilterId = useId();
  const searchInputId = useId();

  // Memoize counts to avoid recalculation
  const { cleanCount, stage1Count, stage2Count, newCount, historicCount, totalCount } = useMemo(() => ({
    cleanCount: projects.filter(p => p.status === 'clean').length,
    stage1Count: projects.filter(p => p.status === 'stage1').length,
    stage2Count: projects.filter(p => p.status === 'stage2').length,
    newCount: projects.filter(p => p.type === 'new').length,
    historicCount: projects.filter(p => p.type === 'historic').length,
    totalCount: projects.length,
  }), [projects]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      if (statusFilter && project.status !== statusFilter) return false;
      if (typeFilter && project.type !== typeFilter) return false;
      if (categoryFilter && project.category !== categoryFilter) return false;
      if (searchTerm) {
        const searchableText = `${project.id} ${project.name} ${project.category}`.toLowerCase();
        if (!searchableText.includes(searchTerm.toLowerCase())) return false;
      }
      return true;
    });
  }, [projects, statusFilter, typeFilter, categoryFilter, searchTerm]);

  // Memoize chart data to prevent unnecessary re-renders
  const statusChartData = useMemo(() => ({
    labels: ['Clean', 'Stage 1 Flagged', 'Stage 2 Flagged'],
    datasets: [{
      data: [cleanCount, stage1Count, stage2Count],
      backgroundColor: ['#059669', '#f59e0b', '#dc2626'],
      borderWidth: 0,
    }],
  }), [cleanCount, stage1Count, stage2Count]);

  const typeChartData = useMemo(() => ({
    labels: ['New Projects', 'Historic Projects'],
    datasets: [{
      label: 'Count',
      data: [newCount, historicCount],
      backgroundColor: ['#06b6d4', '#f59e0b'],
      borderWidth: 0,
      borderRadius: 4,
    }],
  }), [newCount, historicCount]);

  const chartOptions = useMemo<ChartOptions<'doughnut'>>(() => ({
    responsive: true,
    maintainAspectRatio: true,
    onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const statuses: ValidationStatus[] = ['clean', 'stage1', 'stage2'];
        setStatusFilter(statuses[index]);
      }
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { padding: 12, usePointStyle: true },
      },
    },
  }), []);

  const barOptions = useMemo<ChartOptions<'bar'>>(() => ({
    responsive: true,
    maintainAspectRatio: true,
    onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const types: ProjectType[] = ['new', 'historic'];
        setTypeFilter(types[index]);
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
      x: { grid: { display: false } },
    },
    plugins: { legend: { display: false } },
  }), []);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Control Tower - Validation Overview</h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" role="region" aria-label="Key performance indicators">
        <button
          onClick={() => setStatusFilter('')}
          className={`bg-white rounded-lg border p-6 text-left transition-all hover:shadow-md ${statusFilter === '' ? 'border-cyan-500 ring-2 ring-cyan-200' : 'border-gray-200'}`}
          aria-pressed={statusFilter === ''}
        >
          <div className="text-3xl font-bold text-gray-900">{totalCount}</div>
          <div className="text-sm text-gray-500">Total Projects</div>
        </button>
        <button
          onClick={() => setStatusFilter('clean')}
          className={`bg-white rounded-lg border p-6 text-left transition-all hover:shadow-md ${statusFilter === 'clean' ? 'border-green-500 ring-2 ring-green-200' : 'border-green-200'}`}
          aria-pressed={statusFilter === 'clean'}
        >
          <div className="text-3xl font-bold text-green-600">{cleanCount}</div>
          <div className="text-sm text-gray-500">Clean / Ready</div>
          <div className="text-xs text-green-600 mt-1">{totalCount > 0 ? ((cleanCount / totalCount) * 100).toFixed(0) : 0}%</div>
        </button>
        <button
          onClick={() => setStatusFilter('stage1')}
          className={`bg-white rounded-lg border p-6 text-left transition-all hover:shadow-md ${statusFilter === 'stage1' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-200'}`}
          aria-pressed={statusFilter === 'stage1'}
        >
          <div className="text-3xl font-bold text-amber-600">{stage1Count}</div>
          <div className="text-sm text-gray-500">Stage 1 Flagged</div>
          <div className="text-xs text-amber-600 mt-1">{totalCount > 0 ? ((stage1Count / totalCount) * 100).toFixed(0) : 0}%</div>
        </button>
        <button
          onClick={() => setStatusFilter('stage2')}
          className={`bg-white rounded-lg border p-6 text-left transition-all hover:shadow-md ${statusFilter === 'stage2' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-200'}`}
          aria-pressed={statusFilter === 'stage2'}
        >
          <div className="text-3xl font-bold text-red-600">{stage2Count}</div>
          <div className="text-sm text-gray-500">Stage 2 Flagged</div>
          <div className="text-xs text-red-600 mt-1">{totalCount > 0 ? ((stage2Count / totalCount) * 100).toFixed(0) : 0}%</div>
        </button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Validation Status Distribution</h3>
          <div className="h-64 flex items-center justify-center" role="img" aria-label="Doughnut chart showing validation status distribution">
            <Doughnut data={statusChartData} options={chartOptions} />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Types</h3>
          <div className="flex gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">New</span>
              <span className="text-sm text-gray-600">{newCount} projects</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Historic</span>
              <span className="text-sm text-gray-600">{historicCount} projects</span>
            </div>
          </div>
          <div className="h-48" role="img" aria-label="Bar chart showing project types distribution">
            <Bar data={typeChartData} options={barOptions} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Projects</h3>
        <div className="flex flex-wrap gap-4">
          <div>
            <label htmlFor={statusFilterId} className="sr-only">Filter by status</label>
            <select
              id={statusFilterId}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as FilterStatus)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Status</option>
              <option value="clean">Clean</option>
              <option value="stage1">Stage 1 Flagged</option>
              <option value="stage2">Stage 2 Flagged</option>
            </select>
          </div>
          <div>
            <label htmlFor={typeFilterId} className="sr-only">Filter by type</label>
            <select
              id={typeFilterId}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as FilterType)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Types</option>
              <option value="new">New Projects</option>
              <option value="historic">Historic Projects</option>
            </select>
          </div>
          <div>
            <label htmlFor={categoryFilterId} className="sr-only">Filter by category</label>
            <select
              id={categoryFilterId}
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value as FilterCategory)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Categories</option>
              <option value="reforestation">Reforestation</option>
              <option value="wetland">Wetland Restoration</option>
              <option value="efficiency">Water Efficiency</option>
              <option value="infrastructure">Infrastructure</option>
            </select>
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <label htmlFor={searchInputId} className="sr-only">Search projects</label>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
            <input
              id={searchInputId}
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Projects ({filteredProjects.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Projects list">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project ID</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProjects.map(project => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{project.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.type === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {project.type === 'new' ? 'New' : 'Historic'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatCategory(project.category)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      project.status === 'clean' ? 'bg-green-100 text-green-800' :
                      project.status === 'stage1' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {formatStatus(project.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.status === 'stage1' ? 'Stage 1' : project.status === 'stage2' ? 'Stage 2' : project.status === 'clean' ? 'Eligible' : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => onViewProject(project.id)}
                        className="p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                        aria-label={`View details for ${project.name}`}
                      >
                        <Eye className="w-4 h-4" aria-hidden="true" />
                      </button>
                      {project.hasComparison && (
                        <button
                          onClick={() => onViewComparison(project.id)}
                          className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          aria-label={`Compare data for ${project.name}`}
                        >
                          <GitCompare className="w-4 h-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ControlTower;
